import { useTranslation } from 'react-i18next'
import { Layers } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { EstatePart, EstatePartOptions, PartDestination, PartFallback, ScenarioPartPlan, ScenarioTemplateKey, ScenarioTemplates } from '../api'

/**
 * Where each part of the estate goes in this scenario (Andrew, 14 Oct 2026: writing every
 * combination as its own scenario does not scale - "I need it in the application").
 *
 * One row per part: where it recovers to, where instead if that is compromised, and the parts it
 * must reach afterwards. A part left "not planned" is judged by the scenario's hierarchy and what
 * it depends on - there is no scenario-wide "Recovers to" (15 Oct 2026). The clouds it may use are chosen here; which one is used is decided
 * at recovery time. A template fills the rows in one go; locations are then chosen.
 */
export interface PlanValue { parts: ScenarioPartPlan[]; clouds: number[] }
export const TEMPLATE_KEYS: ScenarioTemplateKey[] = ['back_to_production', 'platform_carve_out', 'hybrid_split', 'standby_site', 'cloud_to_cloud', 'clean_room']
const DESTINATIONS: PartDestination[] = ['in_place', 'location', 'cloud', 'vendor', 'not_recovered']
const FALLBACKS: PartFallback[] = ['in_place', 'location', 'cloud']
const NONE = '__none__'

/** Fill the plan from a template: seeded parts by their key; parts the customer added are left as they are. */
export function applyTemplate(template: ScenarioTemplates[ScenarioTemplateKey], parts: EstatePart[], current: PlanValue): PlanValue {
    const byKey = new Map(parts.filter((p) => p.shippedKey).map((p) => [p.shippedKey as string, p.id]))
    const rows = new Map(current.parts.map((r) => [r.partId, r]))
    for (const [key, spec] of Object.entries(template.parts)) {
        const partId = byKey.get(key)
        if (partId == null) continue
        const before = rows.get(partId)
        rows.set(partId, {
            partId,
            recoversTo: spec.to,
            locationId: spec.to === 'location' ? before?.locationId ?? null : null,
            fallbackTo: spec.fallback ?? null,
            fallbackLocationId: spec.fallback === 'location' ? before?.fallbackLocationId ?? null : null,
            connectsTo: (spec.connectsTo ?? []).map((k) => byKey.get(k)).filter((id): id is number => id != null),
        })
    }
    const order = new Map(parts.map((p, i) => [p.id, i]))
    return { ...current, parts: [...rows.values()].sort((a, b) => (order.get(a.partId) ?? 0) - (order.get(b.partId) ?? 0)) }
}

/** What is missing before the plan can be saved, in words; null when it can be. */
export function planProblem(value: PlanValue, t: (key: string, fallback: string, options?: Record<string, unknown>) => string, partName: (id: number) => string): string | null {
    for (const r of value.parts) {
        if (r.recoversTo === 'location' && r.locationId == null) return t('recoveryScenarios.parts.needLocation', 'Choose where {{part}} recovers to.', { part: partName(r.partId) })
        if (r.fallbackTo === 'location' && r.fallbackLocationId == null) return t('recoveryScenarios.parts.needFallbackLocation', 'Choose where {{part}} goes if that is compromised.', { part: partName(r.partId) })
        if ((r.recoversTo === 'cloud' || r.fallbackTo === 'cloud') && value.clouds.length === 0) return t('recoveryScenarios.parts.needCloud', 'Allow at least one cloud: {{part}} recovers to a cloud.', { part: partName(r.partId) })
    }
    return null
}

export function ScenarioPartsPlan({ value, onChange, parts, options, templates, template, onTemplate, dataResidency, onDataResidency, networkPath, onNetworkPath }: {
    value: PlanValue
    onChange: (next: PlanValue) => void
    parts: EstatePart[]
    options: EstatePartOptions | undefined
    templates: ScenarioTemplates | undefined
    template: ScenarioTemplateKey | null
    onTemplate: (key: ScenarioTemplateKey) => void
    dataResidency: string
    onDataResidency: (next: string) => void
    networkPath: string
    onNetworkPath: (next: string) => void
}) {
    const { t } = useTranslation()
    const locations = options?.location ?? []
    const clouds = locations.some((l) => l.cloud) ? locations.filter((l) => l.cloud) : locations
    const rowOf = (partId: number) => value.parts.find((r) => r.partId === partId) ?? null
    const setRow = (partId: number, next: ScenarioPartPlan | null) => {
        const others = value.parts.filter((r) => r.partId !== partId)
        const order = new Map(parts.map((p, i) => [p.id, i]))
        onChange({ ...value, parts: (next ? [...others, next] : others).sort((a, b) => (order.get(a.partId) ?? 0) - (order.get(b.partId) ?? 0)) })
    }
    const destinationLabel = (d: PartDestination | PartFallback) => ({
        in_place: t('recoveryScenarios.parts.inPlace', 'In place'),
        location: t('recoveryScenarios.parts.location', 'A location'),
        cloud: t('recoveryScenarios.parts.cloud', 'A cloud, chosen at recovery'),
        vendor: t('recoveryScenarios.parts.vendor', 'The vendor recovers it'),
        not_recovered: t('recoveryScenarios.parts.notRecovered', 'Not recovered by this scenario'),
    })[d]
    const templateLabel = (k: ScenarioTemplateKey) => ({
        back_to_production: t('recoveryScenarios.templates.back_to_production', 'Back to production'),
        platform_carve_out: t('recoveryScenarios.templates.platform_carve_out', 'Platform carve-out'),
        hybrid_split: t('recoveryScenarios.templates.hybrid_split', 'Hybrid split: core stays, distributed to cloud'),
        standby_site: t('recoveryScenarios.templates.standby_site', 'Standby site'),
        cloud_to_cloud: t('recoveryScenarios.templates.cloud_to_cloud', 'Cloud to cloud'),
        clean_room: t('recoveryScenarios.templates.clean_room', 'Clean room'),
    })[k]
    const locationSelect = (current: number | null, onPick: (id: number) => void, label: string) => (
        <Select value={current == null ? '' : String(current)} onValueChange={(v) => onPick(Number(v))}>
            <SelectTrigger className="h-8 text-xs" aria-label={label}><SelectValue placeholder={t('recoveryScenarios.chooseLocation', 'Choose a location')} /></SelectTrigger>
            <SelectContent className="max-h-[40vh]">{locations.map((l) => <SelectItem key={l.refId} value={String(l.refId)}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
    )

    return (
        <div className="space-y-3 rounded-lg border p-3" data-testid="scenario-parts-plan">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <Label className="flex items-center gap-1.5"><Layers className="h-4 w-4" aria-hidden="true" />{t('recoveryScenarios.parts.title', 'Where each part of the estate goes')}</Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t('recoveryScenarios.parts.helpNoRecoversTo', 'Optional. A part not planned is judged by the scenario\'s hierarchy and what it depends on. The impact assessment checks each planned part: its destination, its fallback, and what it must reach.')}</p>
                </div>
                {templates && (
                    <Select value={template ?? ''} onValueChange={(v) => onTemplate(v as ScenarioTemplateKey)}>
                        <SelectTrigger className="h-8 w-64 text-xs" aria-label={t('recoveryScenarios.parts.template', 'Start from a template')} data-testid="scenario-template">
                            <SelectValue placeholder={t('recoveryScenarios.parts.template', 'Start from a template')} />
                        </SelectTrigger>
                        <SelectContent>{TEMPLATE_KEYS.filter((k) => templates[k]).map((k) => <SelectItem key={k} value={k}>{templateLabel(k)}</SelectItem>)}</SelectContent>
                    </Select>
                )}
            </div>

            {parts.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('recoveryScenarios.parts.noParts', 'No parts of the estate are defined yet. Use "Parts of the estate" on the Recovery Scenarios screen.')}</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                        <thead>
                            <tr className="text-left text-xs text-muted-foreground">
                                <th className="py-1 pr-2 font-medium">{t('recoveryScenarios.parts.part', 'Part of the estate')}</th>
                                <th className="py-1 pr-2 font-medium">{t('recoveryScenarios.recoversTo', 'Recovers to')}</th>
                                <th className="py-1 pr-2 font-medium">{t('recoveryScenarios.parts.fallback', 'If that is compromised')}</th>
                                <th className="py-1 font-medium">{t('recoveryScenarios.parts.connectsTo', 'Connects back to')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parts.map((part) => {
                                const row = rowOf(part.id)
                                return (
                                    <tr key={part.id} className="border-t align-top" data-testid={`plan-part-${part.id}`}>
                                        <td className="py-2 pr-2">
                                            <span className="font-medium">{part.name}</span>
                                            <span className="block text-xs text-muted-foreground">{t('recoveryScenarios.parts.objects', '{{count}} objects', { count: part.objects })}</span>
                                        </td>
                                        <td className="space-y-1 py-2 pr-2">
                                            <Select
                                                value={row?.recoversTo ?? NONE}
                                                onValueChange={(v) => setRow(part.id, v === NONE ? null : {
                                                    partId: part.id, recoversTo: v as PartDestination,
                                                    locationId: v === 'location' ? row?.locationId ?? null : null,
                                                    fallbackTo: v === 'vendor' || v === 'not_recovered' ? null : row?.fallbackTo ?? null,
                                                    fallbackLocationId: v === 'vendor' || v === 'not_recovered' ? null : row?.fallbackLocationId ?? null,
                                                    connectsTo: row?.connectsTo ?? [],
                                                })}
                                            >
                                                <SelectTrigger className="h-8 text-xs" aria-label={t('recoveryScenarios.parts.recoversToFor', 'Where {{part}} recovers to', { part: part.name })}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={NONE}>{t('recoveryScenarios.parts.notPlannedPlain', 'Not planned')}</SelectItem>
                                                    {DESTINATIONS.map((d) => <SelectItem key={d} value={d}>{destinationLabel(d)}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                            {row?.recoversTo === 'location' && locationSelect(row.locationId, (id) => setRow(part.id, { ...row, locationId: id }), t('recoveryScenarios.parts.locationFor', 'Location for {{part}}', { part: part.name }))}
                                        </td>
                                        <td className="space-y-1 py-2 pr-2">
                                            {row && row.recoversTo !== 'vendor' && row.recoversTo !== 'not_recovered' ? (
                                                <>
                                                    <Select value={row.fallbackTo ?? NONE} onValueChange={(v) => setRow(part.id, { ...row, fallbackTo: v === NONE ? null : v as PartFallback, fallbackLocationId: v === 'location' ? row.fallbackLocationId : null })}>
                                                        <SelectTrigger className="h-8 text-xs" aria-label={t('recoveryScenarios.parts.fallbackFor', 'Where {{part}} goes if that is compromised', { part: part.name })}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value={NONE}>{t('recoveryScenarios.parts.noFallback', 'Nowhere: the scenario cannot be used')}</SelectItem>
                                                            {FALLBACKS.filter((f) => f !== row.recoversTo || f === 'location').map((f) => <SelectItem key={f} value={f}>{destinationLabel(f)}</SelectItem>)}
                                                        </SelectContent>
                                                    </Select>
                                                    {row.fallbackTo === 'location' && locationSelect(row.fallbackLocationId, (id) => setRow(part.id, { ...row, fallbackLocationId: id }), t('recoveryScenarios.parts.fallbackLocationFor', 'Fallback location for {{part}}', { part: part.name }))}
                                                </>
                                            ) : <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                        <td className="py-2">
                                            {row && row.recoversTo !== 'not_recovered' ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {parts.filter((p) => p.id !== part.id).map((p) => {
                                                        const on = row.connectsTo.includes(p.id)
                                                        return (
                                                            <button
                                                                key={p.id}
                                                                type="button"
                                                                aria-pressed={on}
                                                                onClick={() => setRow(part.id, { ...row, connectsTo: on ? row.connectsTo.filter((id) => id !== p.id) : [...row.connectsTo, p.id] })}
                                                                className={cn('rounded-full border px-2 py-0.5 text-xs', on ? 'border-primary bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted')}
                                                            >
                                                                {p.name}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            ) : <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="space-y-1.5">
                <Label>{t('recoveryScenarios.parts.clouds', 'Clouds it may use')}</Label>
                <div className="flex flex-wrap gap-1" data-testid="scenario-clouds">
                    {clouds.map((l) => {
                        const on = value.clouds.includes(l.refId)
                        return (
                            <button key={l.refId} type="button" aria-pressed={on}
                                onClick={() => onChange({ ...value, clouds: on ? value.clouds.filter((c) => c !== l.refId) : [...value.clouds, l.refId] })}
                                className={cn('rounded-full border px-2 py-0.5 text-xs', on ? 'border-primary bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted')}>
                                {l.name}
                            </button>
                        )
                    })}
                </div>
                <p className="text-xs text-muted-foreground">{t('recoveryScenarios.parts.cloudsHelp', 'Which of these is used is chosen at recovery time. The assessment rules the scenario out only if every one is compromised.')}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1 text-sm font-medium">
                    {t('recoveryScenarios.parts.dataResidency', 'Data residency')}
                    <Input value={dataResidency} maxLength={500} onChange={(e) => onDataResidency(e.target.value)} placeholder={t('recoveryScenarios.parts.dataResidencyExample', 'For example: UK and EU regions only')} />
                </label>
                <label className="block space-y-1 text-sm font-medium">
                    {t('recoveryScenarios.parts.networkPath', 'Network path back')}
                    <Input value={networkPath} maxLength={1000} onChange={(e) => onNetworkPath(e.target.value)} placeholder={t('recoveryScenarios.parts.networkPathExample', 'For example: site-to-site VPN to London')} />
                </label>
            </div>
        </div>
    )
}
